import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getRemainingExpiration } from "../../../utils/getOTPExpirationTime";

export const RegisterFormContext = React.createContext({
  email: "",
  firstStepSubmit: () => {},
  unsetEmail: () => {},
  otp: "",
  finishOTP: () => {},
  phone: "",
  phoneStepSubmit: () => {},
  userInfo: {
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    nationality: "",
  },
  userInfoSubmit: () => {},
  steps: [],
  activeStep: 1,
  setActiveStep: () => {},
});

/**
 * A provider that is used to wrap the data for the registeration process.
 * It stores the following values:
 *  - email: The email the user has entered
 *  - otp: The OTP the user has entered.
 *  - phone: The phone the user has entered.
 *  - steps: A memoized object of the steps that will be displayed to the user. Depends on whether the current user is a guest trying to create an account or an agent that is creating a new user.
 *  - userInfo: The user information (name, nationality, dob, ...etc) that has been submitted
 *  - activeStep: A state that represents which step the user is currently at.
 *  - timer: The time remaining to send OTP
 * It also returns the following actions:
 *  - firstStepSubmit: A function to be called when the user finishes the first step, the one with the email.
 *  - finishOTP: A function to be called when the user finishes the otp step.
 *  - phoneStepSubmit: A function to be called when the user submits their phone number.
 *  - userInfoSubmit: A function to be called when the user submits their info. It sends a request to the registeration endpoint
 *  - unsetEmail: A function that unsets the email to return the user to the first step
 *  - setTimer: A setter for the timer state
 *  - setActiveStep: A setter for the active step state
 *
 * @param {Object} props
 * @param {JSX.Element | JSX.Element[]} props.children - The elements inside the context provider
 * @returns {JSX.Element}
 */

export const RegisterFormProvider = ({ children }) => {
  const [timer, setTimer] = useState(getRemainingExpiration()); // Timer state
  const navigate = useNavigate();
  const user = useSelector((state) => state.authData.user);
  const { state } = useLocation();
  const email = state?.registerEmail;
  const otp = state?.otp;
  const phone = state?.registerPhone;
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    nationality: "",
  });
  const [activeStep, setActiveStep] = useState(1);
  const firstStepSubmit = (email) => {
    navigate(window.location.pathname, {
      state: { registerEmail: email },
      replace: true,
    });
  };
  const finishOTP = (otp) => {
    navigate(window.location.pathname, {
      state: { otp, registerEmail: email },
      replace: true,
    });
  };
  const phoneStepSubmit = (phone) => {
    navigate(window.location.pathname, {
      state: { otp, registerEmail: email, registerPhone: phone },
      replace: true,
    });
  };
  const userInfoSubmit = (submittedUserInfo) => {
    setUserInfo(submittedUserInfo);
    setActiveStep(2);
  };
  const { t } = useTranslation("global");
  const steps = useMemo(
    () =>
      user?.agent
        ? [
            { id: 1, text: t("my_profile.user_info_text") },
            { id: 2, text: t("my_profile.city_info_text") },
          ]
        : [
            { id: 1, text: t("my_profile.user_info_text") },
            { id: 2, text: t("my_profile.security_info_text") },
          ],
    [t, user]
  );
  const unsetEmail = () => {
    navigate(window.location.pathname, {
      state: {},
      replace: true,
    });
  };
  return (
    <RegisterFormContext.Provider
      value={{
        timer,
        setTimer,
        email,
        firstStepSubmit,
        otp,
        finishOTP,
        phone,
        phoneStepSubmit,
        userInfo,
        userInfoSubmit,
        steps,
        activeStep,
        setActiveStep,
        unsetEmail,
      }}
    >
      {children}
    </RegisterFormContext.Provider>
  );
};
